<script setup lang="ts">
import { Coins, ExternalLink, LoaderCircle, ReceiptText } from 'lucide-vue-next'
import { httpsCallable } from 'firebase/functions'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import CreditPacks from '@/components/CreditPacks.vue'
import { firebaseClient } from '@/firebase'
import { useUsageStore } from '@/stores/domain'

const { credits, month, ledger, balance } = storeToRefs(useUsageStore())
const purchased = new URLSearchParams(window.location.search).get('purchase') === 'success'
const history = computed(() => ledger.value.filter((entry) => entry.type === 'purchase' || entry.type === 'grant').slice(0, 20))
const busy = ref(false)
const error = ref('')
const formatted = (value: number) => Math.round(value).toLocaleString('en-US')
const stamp = (value: number) => new Date(value).toLocaleDateString(undefined, { dateStyle: 'medium' })

async function openPortal(): Promise<void> {
  busy.value = true; error.value = ''
  try {
    const callable = httpsCallable<{ returnUrl: string }, { url: string }>(firebaseClient.functions, 'createBillingPortalSession')
    const result = await callable({ returnUrl: `${window.location.origin}/app/billing` })
    window.location.assign(result.data.url)
  } catch (caught) { error.value = caught instanceof Error ? caught.message.replace('Firebase: ', '') : 'Billing is unavailable.'; busy.value = false }
}
</script>

<template><div class="section-page">
  <header class="section-page__header"><div><span class="eyebrow">Credits</span><h1>Billing</h1><p>Buy credits to run your Manager and its agents. Every plan shows its estimated credits before you approve it, and usage is charged as the work happens.</p></div></header>
  <p v-if="purchased" class="form-success">Payment received. Your credits appear here as soon as the payment is confirmed.</p>
  <section class="detail-grid">
    <article class="detail-card"><div class="detail-card__head"><div><span class="eyebrow">Balance</span><h2 class="credit-balance" :class="{ 'credit-balance--low': balance <= 0 }">{{ formatted(balance) }} credits</h2></div><Coins :size="18" /></div>
      <p v-if="balance <= 0">Your balance is empty, so new work is paused. Buy credits below to resume queued work.</p>
      <p v-else>{{ formatted(month?.creditsUsed ?? 0) }} credits used this month · {{ formatted(credits?.lifetimePurchased ?? 0) }} purchased in total.</p>
    </article>
    <article class="detail-card"><div class="detail-card__head"><div><span class="eyebrow">Receipts</span><h2>Payment history</h2></div><ReceiptText :size="18" /></div>
      <div v-if="history.length" class="team-list"><div v-for="entry in history" :key="entry.id"><span><strong>+{{ formatted(entry.credits) }} credits</strong><small>{{ entry.type === 'grant' ? 'Added by SkipHR' : 'Purchase' }} · {{ stamp(entry.createdAt) }}</small></span></div></div>
      <p v-else>No purchases yet.</p>
      <p v-if="error" class="form-error">{{ error }}</p>
      <button class="button" type="button" :disabled="busy" @click="openPortal"><LoaderCircle v-if="busy" :size="15" class="spin" /><ExternalLink v-else :size="15" /> Invoices and payment methods</button>
    </article>
  </section>
  <section class="detail-card detail-card--wide"><div class="detail-card__head"><div><span class="eyebrow">Top up</span><h2>Buy credits</h2></div></div><CreditPacks /></section>
</div></template>
